import React, { useState, useEffect } from 'react';
import {
  Building2,
  RefreshCw,
  FileText,
  Settings,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  Link2,
  Unlink,
  Users,
  Monitor,
  Shield,
  Cloud,
  Mail,
  HardDrive,
  Loader2,
  Search,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';

const APP_VERSION = '1.0.0';

// API helper
const api = {
  async request(endpoint, options = {}) {
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || 'Request failed');
    }
    return response.json();
  },

  // Sync endpoints
  getSyncStatus: () => api.request('/api/sync/status'),
  triggerSync: (platform) => api.request('/api/sync/trigger', {
    method: 'POST',
    body: JSON.stringify({ platform }),
  }),
  testConnections: (platform) => api.request('/api/sync/test-connections', {
    method: 'POST',
    body: JSON.stringify({ platform }),
  }),

  // Customer endpoints
  getCustomers: (search) => api.request(`/api/customers/list${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getCustomer: (id) => api.request(`/api/customers/${id}`),
  createCustomer: (data) => api.request('/api/customers/create', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateCustomer: (id, data) => api.request(`/api/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Mapping endpoints
  getMappings: (platform, unmapped) => {
    const params = new URLSearchParams();
    if (platform) params.set('platform', platform);
    if (unmapped) params.set('unmapped', 'true');
    return api.request(`/api/mappings/list?${params}`);
  },
  createMapping: (data) => api.request('/api/mappings/create', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  deleteMapping: (id) => api.request(`/api/mappings/${id}`, { method: 'DELETE' }),
  autoMatch: (minConfidence) => api.request('/api/mappings/auto-match', {
    method: 'POST',
    body: JSON.stringify({ minConfidence }),
  }),

  // Report endpoints
  generateReport: (options) => api.request('/api/reports/generate', {
    method: 'POST',
    body: JSON.stringify(options),
  }),
  getReports: () => api.request('/api/reports/list'),
  getReport: (id) => api.request(`/api/reports/${id}`),

  // Pricing endpoints
  getPricing: () => api.request('/api/pricing/list'),
  updatePricing: (data) => api.request('/api/pricing/update', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Discrepancy endpoints
  getDiscrepancies: (params = {}) => {
    const p = new URLSearchParams();
    if (params.customerId) p.set('customerId', params.customerId);
    if (params.severity) p.set('severity', params.severity);
    if (params.type) p.set('type', params.type);
    if (params.resolved !== undefined) p.set('resolved', String(params.resolved));
    return api.request(`/api/discrepancies/list?${p}`);
  },
  resolveDiscrepancy: (id, resolvedBy, notes) => api.request('/api/discrepancies/resolve', {
    method: 'POST',
    body: JSON.stringify({ id, resolvedBy, notes }),
  }),

  // User endpoint
  getUser: () => api.request('/api/user'),

  // Logs endpoint
  getLogs: (limit, offset) => api.request(`/api/logs/list?limit=${limit}&offset=${offset}`),
};

// Platform icons and colors
const PLATFORM_CONFIG = {
  datto_rmm: { name: 'Datto RMM', icon: Monitor, color: 'bg-blue-500', licenseType: 'Devices' },
  datto_edr: { name: 'Datto EDR', icon: Shield, color: 'bg-purple-500', licenseType: 'Endpoints' },
  datto_saas: { name: 'Datto SaaS Protection', icon: Cloud, color: 'bg-cyan-500', licenseType: 'Protected Users' },
  rocketcyber: { name: 'Rocket Cyber', icon: Shield, color: 'bg-red-500', licenseType: 'Agents' },
  threatlocker: { name: 'ThreatLocker', icon: Shield, color: 'bg-orange-500', licenseType: 'Computers' },
  saas_alerts: { name: 'SaaS Alerts', icon: AlertCircle, color: 'bg-yellow-500', licenseType: 'Users' },
  pax8: { name: 'Pax8 (M365)', icon: Mail, color: 'bg-green-500', licenseType: 'M365 Users' },
  autotask: { name: 'Autotask PSA', icon: Building2, color: 'bg-indigo-600', licenseType: 'Contracts' },
  itglue: { name: 'IT Glue', icon: FileText, color: 'bg-teal-500', licenseType: 'Organizations' },
  darkwebid: { name: 'DarkwebID', icon: Shield, color: 'bg-slate-600', licenseType: 'Monitored Emails' },
  bullphishid: { name: 'BullphishID', icon: Mail, color: 'bg-pink-500', licenseType: 'Users' },
  inky: { name: 'Inky', icon: Mail, color: 'bg-violet-500', licenseType: 'Mailboxes' },
  cipp: { name: 'CIPP (M365)', icon: Cloud, color: 'bg-blue-400', licenseType: 'M365 Users' },
};

// Components
function LoadingSpinner({ size = 'md' }) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };
  return <Loader2 className={`${sizeClasses[size]} animate-spin text-blue-400`} />;
}

function ErrorMessage({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="bg-red-900/50 border border-red-500 rounded-lg p-4 flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-red-200">{message}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-red-400 hover:text-red-300">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

function SuccessMessage({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="bg-green-900/50 border border-green-500 rounded-lg p-4 flex items-start gap-3">
      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-green-200">{message}</div>
      {onDismiss && (
        <button onClick={onDismiss} className="text-green-400 hover:text-green-300">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

function StatusBadge({ success, syncing }) {
  if (syncing) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-900/50 text-blue-300">
        <Loader2 className="w-3 h-3 animate-spin" /> Syncing
      </span>
    );
  }
  if (success === true) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-900/50 text-green-300">
        <CheckCircle2 className="w-3 h-3" /> OK
      </span>
    );
  }
  if (success === false) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-900/50 text-red-300">
        <AlertCircle className="w-3 h-3" /> Error
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-slate-700 text-slate-400">
      <Clock className="w-3 h-3" /> Not synced
    </span>
  );
}

function SeverityBadge({ severity }) {
  const styles = {
    high: 'bg-red-900/50 text-red-300',
    medium: 'bg-yellow-900/50 text-yellow-300',
    low: 'bg-slate-700 text-slate-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${styles[severity] || styles.low}`}>
      {severity}
    </span>
  );
}

function PlatformIcon({ platform, className = '' }) {
  const config = PLATFORM_CONFIG[platform];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center ${className}`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
  );
}

// Main App Component
function UnifiedBilling() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dashboard state
  const [syncStatus, setSyncStatus] = useState({});
  const [syncingPlatforms, setSyncingPlatforms] = useState({});

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');

  // Mappings state
  const [unmappedCustomers, setUnmappedCustomers] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [selectedPlatformFilter, setSelectedPlatformFilter] = useState('');
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [mappingSource, setMappingSource] = useState(null);

  // Reports state
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Discrepancies state
  const [discrepancies, setDiscrepancies] = useState([]);
  const [discSummary, setDiscSummary] = useState({ high: 0, medium: 0, low: 0, total: 0 });
  const [discrepanciesLoading, setDiscrepanciesLoading] = useState(false);
  const [discSeverityFilter, setDiscSeverityFilter] = useState('');
  const [discResolvedFilter, setDiscResolvedFilter] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvingDisc, setResolvingDisc] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');

  // Load initial data
  useEffect(() => {
    loadUser();
    loadSyncStatus();
  }, []);

  // Load data when section changes
  useEffect(() => {
    if (activeSection === 'customers') {
      loadCustomers();
    } else if (activeSection === 'mappings') {
      loadUnmappedCustomers();
    } else if (activeSection === 'reports') {
      loadReports();
    } else if (activeSection === 'discrepancies') {
      loadDiscrepancies('', false);
    }
  }, [activeSection]);

  async function loadUser() {
    try {
      const data = await api.getUser();
      setUser(data);
    } catch (err) {
      console.error('Failed to load user:', err);
    }
  }

  async function loadSyncStatus() {
    try {
      const data = await api.getSyncStatus();
      setSyncStatus(data);
    } catch (err) {
      setError('Failed to load sync status: ' + err.message);
    }
  }

  async function handleSync(platform) {
    setSyncingPlatforms(prev => ({ ...prev, [platform || 'all']: true }));
    setError(null);
    try {
      await api.triggerSync(platform);
      setSuccess(`Sync ${platform ? `for ${PLATFORM_CONFIG[platform]?.name}` : 'for all platforms'} completed`);
      await loadSyncStatus();
    } catch (err) {
      setError('Sync failed: ' + err.message);
    } finally {
      setSyncingPlatforms(prev => ({ ...prev, [platform || 'all']: false }));
    }
  }

  async function loadCustomers() {
    setCustomersLoading(true);
    try {
      const data = await api.getCustomers(customerSearch);
      setCustomers(data.customers || []);
    } catch (err) {
      setError('Failed to load customers: ' + err.message);
    } finally {
      setCustomersLoading(false);
    }
  }

  async function loadCustomerDetails(id) {
    try {
      const data = await api.getCustomer(id);
      setCustomerDetails(data);
    } catch (err) {
      setError('Failed to load customer details: ' + err.message);
    }
  }

  async function handleCreateCustomer() {
    if (!newCustomerName.trim()) return;
    try {
      await api.createCustomer({ name: newCustomerName.trim() });
      setSuccess('Customer created successfully');
      setShowNewCustomerModal(false);
      setNewCustomerName('');
      loadCustomers();
    } catch (err) {
      setError('Failed to create customer: ' + err.message);
    }
  }

  async function loadUnmappedCustomers() {
    setMappingsLoading(true);
    try {
      const data = await api.getMappings(selectedPlatformFilter, true);
      setUnmappedCustomers(data.unmapped || []);
    } catch (err) {
      setError('Failed to load unmapped customers: ' + err.message);
    } finally {
      setMappingsLoading(false);
    }
  }

  async function handleAutoMatch() {
    setMappingsLoading(true);
    try {
      const result = await api.autoMatch(0.85);
      setSuccess(`Auto-matched ${result.matchedCount} customers. ${result.unmatchedCount} remain unmatched.`);
      loadUnmappedCustomers();
    } catch (err) {
      setError('Auto-match failed: ' + err.message);
    } finally {
      setMappingsLoading(false);
    }
  }

  async function handleCreateMapping(customerId) {
    if (!mappingSource || !customerId) return;
    try {
      await api.createMapping({
        customerId,
        platform: mappingSource.platform,
        platformCustomerId: mappingSource.platformCustomerId,
        platformCustomerName: mappingSource.name,
      });
      setSuccess('Mapping created successfully');
      setShowMappingModal(false);
      setMappingSource(null);
      loadUnmappedCustomers();
    } catch (err) {
      setError('Failed to create mapping: ' + err.message);
    }
  }

  async function loadReports() {
    setReportsLoading(true);
    try {
      const data = await api.getReports();
      setReports(data.reports || []);
    } catch (err) {
      setError('Failed to load reports: ' + err.message);
    } finally {
      setReportsLoading(false);
    }
  }

  async function handleGenerateReport() {
    setGeneratingReport(true);
    try {
      const report = await api.generateReport({ reportType: 'ad-hoc' });
      setCurrentReport(report);
      setSuccess('Report generated successfully');
      loadReports();
    } catch (err) {
      setError('Failed to generate report: ' + err.message);
    } finally {
      setGeneratingReport(false);
    }
  }

  async function loadReport(id) {
    try {
      const data = await api.getReport(id);
      setCurrentReport(data.data);
    } catch (err) {
      setError('Failed to load report: ' + err.message);
    }
  }

  async function loadDiscrepancies(severity = discSeverityFilter, resolved = discResolvedFilter) {
    setDiscrepanciesLoading(true);
    try {
      const data = await api.getDiscrepancies({ severity: severity || undefined, resolved });
      setDiscrepancies(data.discrepancies || []);
      setDiscSummary(data.summary || { high: 0, medium: 0, low: 0, total: 0 });
    } catch (err) {
      setError('Failed to load discrepancies: ' + err.message);
    } finally {
      setDiscrepanciesLoading(false);
    }
  }

  async function handleResolveDiscrepancy() {
    if (!resolvingDisc) return;
    try {
      await api.resolveDiscrepancy(resolvingDisc.id, user?.email || 'admin', resolveNotes);
      setSuccess('Discrepancy resolved');
      setShowResolveModal(false);
      setResolvingDisc(null);
      setResolveNotes('');
      loadDiscrepancies();
    } catch (err) {
      setError('Failed to resolve discrepancy: ' + err.message);
    }
  }

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: HardDrive },
    { id: 'customers', label: 'Customers', icon: Building2 },
    { id: 'mappings', label: 'Mappings', icon: Link2 },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'discrepancies', label: 'Discrepancies', icon: AlertCircle },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <HardDrive className="w-6 h-6 text-blue-400" />
            Unified Billing
          </h1>
          <p className="text-xs text-slate-500 mt-1">v{APP_VERSION}</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {navItems.map(item => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveSection(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    activeSection === item.id
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {user?.email && (
          <div className="p-4 border-t border-slate-800">
            <div className="text-xs text-slate-500">Logged in as</div>
            <div className="text-sm text-slate-300 truncate">{user.email}</div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8 max-w-7xl">
          {/* Error/Success messages */}
          {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}
          {success && <SuccessMessage message={success} onDismiss={() => setSuccess(null)} />}

          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <div className="space-y-8">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Dashboard</h2>
                <button
                  onClick={() => handleSync()}
                  disabled={syncingPlatforms.all}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed text-white rounded-lg transition-all"
                >
                  {syncingPlatforms.all ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Sync All
                </button>
              </div>

              {/* Platform Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
                  const status = syncStatus[key] || {};
                  const isSyncing = syncingPlatforms[key];
                  return (
                    <div
                      key={key}
                      className="bg-slate-900 border border-slate-800 rounded-xl p-5 hover:border-slate-700 transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <PlatformIcon platform={key} />
                        <StatusBadge success={status.success} syncing={isSyncing} />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-1">{config.name}</h3>
                      <p className="text-sm text-slate-400 mb-4">
                        {status.customersCount !== undefined
                          ? `${status.customersCount} customers`
                          : 'Not synced'}
                      </p>
                      {status.lastSyncAt && (
                        <p className="text-xs text-slate-500 mb-4">
                          Last sync: {new Date(status.lastSyncAt).toLocaleString()}
                        </p>
                      )}
                      <button
                        onClick={() => handleSync(key)}
                        disabled={isSyncing}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800 disabled:cursor-not-allowed text-slate-300 rounded-lg text-sm transition-all"
                      >
                        {isSyncing ? <LoadingSpinner size="sm" /> : <RefreshCw className="w-4 h-4" />}
                        Sync
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Customers Section */}
          {activeSection === 'customers' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Customers</h2>
                <button
                  onClick={() => setShowNewCustomerModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                >
                  <Plus className="w-4 h-4" />
                  New Customer
                </button>
              </div>

              {/* Search */}
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && loadCustomers()}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={loadCustomers}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-all"
                >
                  Search
                </button>
              </div>

              {/* Customers Table */}
              {customersLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-800">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Customer
                        </th>
                        {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                          <th
                            key={key}
                            className="px-4 py-4 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider"
                          >
                            <div className="flex flex-col items-center gap-1">
                              <PlatformIcon platform={key} className="w-6 h-6" />
                              <span className="hidden xl:inline">{config.name.split(' ')[0]}</span>
                            </div>
                          </th>
                        ))}
                        <th className="px-4 py-4 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {customers.map(customer => (
                        <CustomerRow
                          key={customer.id}
                          customer={customer}
                          onSelect={() => {
                            setSelectedCustomer(customer);
                            loadCustomerDetails(customer.id);
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
                  {customers.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      No customers found. Create one or sync your platforms.
                    </div>
                  )}
                </div>
              )}

              {/* Customer Details Modal */}
              {selectedCustomer && customerDetails && (
                <CustomerDetailsModal
                  customer={customerDetails}
                  onClose={() => {
                    setSelectedCustomer(null);
                    setCustomerDetails(null);
                  }}
                />
              )}

              {/* New Customer Modal */}
              {showNewCustomerModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-xl font-bold text-white mb-4">New Customer</h3>
                    <input
                      type="text"
                      placeholder="Customer name"
                      value={newCustomerName}
                      onChange={e => setNewCustomerName(e.target.value)}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 mb-4"
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowNewCustomerModal(false);
                          setNewCustomerName('');
                        }}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateCustomer}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all"
                      >
                        Create
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mappings Section */}
          {activeSection === 'mappings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Customer Mappings</h2>
                <button
                  onClick={handleAutoMatch}
                  disabled={mappingsLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white rounded-lg transition-all"
                >
                  {mappingsLoading ? <LoadingSpinner size="sm" /> : <Link2 className="w-4 h-4" />}
                  Auto-Match
                </button>
              </div>

              {/* Platform Filter */}
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setSelectedPlatformFilter('');
                    loadUnmappedCustomers();
                  }}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${
                    selectedPlatformFilter === ''
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  All Platforms
                </button>
                {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedPlatformFilter(key);
                      setTimeout(loadUnmappedCustomers, 0);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                      selectedPlatformFilter === key
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    <PlatformIcon platform={key} className="w-5 h-5" />
                    {config.name}
                  </button>
                ))}
              </div>

              {/* Unmapped Customers */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl">
                <div className="px-6 py-4 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">
                    Unmapped Platform Customers ({unmappedCustomers.length})
                  </h3>
                  <p className="text-sm text-slate-400">
                    These customers from external platforms need to be mapped to unified customer records.
                  </p>
                </div>
                {mappingsLoading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {unmappedCustomers.map(uc => (
                      <div
                        key={`${uc.platform}-${uc.platformCustomerId}`}
                        className="px-6 py-4 flex items-center justify-between hover:bg-slate-800/50"
                      >
                        <div className="flex items-center gap-4">
                          <PlatformIcon platform={uc.platform} />
                          <div>
                            <div className="text-white font-medium">{uc.name}</div>
                            <div className="text-sm text-slate-500">
                              {PLATFORM_CONFIG[uc.platform]?.name} • ID: {uc.platformCustomerId}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            setMappingSource(uc);
                            setShowMappingModal(true);
                          }}
                          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-all"
                        >
                          <Link2 className="w-4 h-4" />
                          Map
                        </button>
                      </div>
                    ))}
                    {unmappedCustomers.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        All platform customers are mapped!
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mapping Modal */}
              {showMappingModal && mappingSource && (
                <MappingModal
                  source={mappingSource}
                  customers={customers}
                  onSelect={handleCreateMapping}
                  onClose={() => {
                    setShowMappingModal(false);
                    setMappingSource(null);
                  }}
                  onLoadCustomers={loadCustomers}
                />
              )}
            </div>
          )}

          {/* Reports Section */}
          {activeSection === 'reports' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Billing Reports</h2>
                <button
                  onClick={handleGenerateReport}
                  disabled={generatingReport}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white rounded-lg transition-all"
                >
                  {generatingReport ? <LoadingSpinner size="sm" /> : <FileText className="w-4 h-4" />}
                  Generate Report
                </button>
              </div>

              {/* Current Report */}
              {currentReport && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl">
                  <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        Report: {currentReport.reportDate}
                      </h3>
                      <p className="text-sm text-slate-400">
                        Generated {new Date(currentReport.generatedAt).toLocaleString()}
                      </p>
                    </div>
                    <a
                      href={`/api/reports/export?reportId=${currentReport.reportId}&format=csv`}
                      className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Export CSV
                    </a>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-800">
                          <th className="px-6 py-3 text-left text-xs font-semibold text-slate-400 uppercase">
                            Customer
                          </th>
                          {currentReport.platforms.map(p => (
                            <th
                              key={p.key}
                              className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase"
                            >
                              {p.name}
                            </th>
                          ))}
                          <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {currentReport.customers.map(customer => (
                          <tr key={customer.id} className="hover:bg-slate-800/50">
                            <td className="px-6 py-4 text-white font-medium">
                              {customer.displayName || customer.name}
                            </td>
                            {currentReport.platforms.map(p => {
                              const platformData = customer.platforms[p.key];
                              const count = platformData
                                ? platformData.licenses.reduce((sum, l) => sum + l.count, 0)
                                : 0;
                              return (
                                <td key={p.key} className="px-4 py-4 text-center text-slate-300">
                                  {count || '-'}
                                </td>
                              );
                            })}
                            <td className="px-4 py-4 text-center text-white font-semibold">
                              {customer.totalLicenses}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-800 font-semibold">
                          <td className="px-6 py-4 text-white">TOTALS</td>
                          {currentReport.platforms.map(p => (
                            <td key={p.key} className="px-4 py-4 text-center text-white">
                              {currentReport.summary.platformTotals[p.key]?.total || 0}
                            </td>
                          ))}
                          <td className="px-4 py-4 text-center text-white">
                            {currentReport.customers.reduce((sum, c) => sum + c.totalLicenses, 0)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Past Reports */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl">
                <div className="px-6 py-4 border-b border-slate-800">
                  <h3 className="text-lg font-semibold text-white">Past Reports</h3>
                </div>
                {reportsLoading ? (
                  <div className="flex justify-center py-12">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : (
                  <div className="divide-y divide-slate-800">
                    {reports.map(report => (
                      <div
                        key={report.id}
                        className="px-6 py-4 flex items-center justify-between hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => loadReport(report.id)}
                      >
                        <div>
                          <div className="text-white font-medium">{report.reportDate}</div>
                          <div className="text-sm text-slate-500">
                            {report.totalCustomers} customers • {report.reportType}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-500" />
                      </div>
                    ))}
                    {reports.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        No reports generated yet.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Discrepancies Section */}
          {activeSection === 'discrepancies' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Discrepancies</h2>
                <button
                  onClick={() => loadDiscrepancies()}
                  disabled={discrepanciesLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800 text-white rounded-lg transition-all"
                >
                  {discrepanciesLoading ? <LoadingSpinner size="sm" /> : <RefreshCw className="w-4 h-4" />}
                  Refresh
                </button>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'high', label: 'High', color: 'border-red-500 bg-red-900/20', textColor: 'text-red-400' },
                  { key: 'medium', label: 'Medium', color: 'border-yellow-500 bg-yellow-900/20', textColor: 'text-yellow-400' },
                  { key: 'low', label: 'Low', color: 'border-slate-500 bg-slate-800', textColor: 'text-slate-300' },
                ].map(({ key, label, color, textColor }) => (
                  <button
                    key={key}
                    onClick={() => {
                      const next = discSeverityFilter === key ? '' : key;
                      setDiscSeverityFilter(next);
                      loadDiscrepancies(next, discResolvedFilter);
                    }}
                    className={`border rounded-xl p-5 text-left transition-all hover:opacity-80 ${color} ${discSeverityFilter === key ? 'ring-2 ring-white/20' : ''}`}
                  >
                    <div className={`text-3xl font-bold ${textColor}`}>{discSummary[key] || 0}</div>
                    <div className="text-sm text-slate-400 mt-1">{label} severity</div>
                  </button>
                ))}
              </div>

              {/* Filter Bar */}
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => {
                    setDiscSeverityFilter('');
                    loadDiscrepancies('', discResolvedFilter);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm transition-all ${discSeverityFilter === '' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                >
                  All Severities
                </button>
                {['high', 'medium', 'low'].map(s => (
                  <button
                    key={s}
                    onClick={() => {
                      const next = discSeverityFilter === s ? '' : s;
                      setDiscSeverityFilter(next);
                      loadDiscrepancies(next, discResolvedFilter);
                    }}
                    className={`px-4 py-2 rounded-lg text-sm capitalize transition-all ${discSeverityFilter === s ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                  >
                    {s}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-2">
                  <label className="flex items-center gap-2 text-slate-400 text-sm cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={discResolvedFilter}
                      onChange={e => {
                        setDiscResolvedFilter(e.target.checked);
                        loadDiscrepancies(discSeverityFilter, e.target.checked);
                      }}
                      className="rounded"
                    />
                    Show resolved
                  </label>
                </div>
              </div>

              {/* Discrepancies Table */}
              {discrepanciesLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-800">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Entity</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Platforms</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">Severity</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Detected</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {discrepancies.map(disc => (
                        <tr key={disc.id} className={`hover:bg-slate-800/50 ${disc.is_resolved ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3 text-white text-sm font-medium">{disc.customer_name || disc.customer_id}</td>
                          <td className="px-4 py-3 text-slate-300 text-sm">
                            <span className="font-mono text-xs bg-slate-800 px-2 py-1 rounded">
                              {disc.discrepancy_type?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300 text-sm font-mono">{disc.entity_name || '—'}</td>
                          <td className="px-4 py-3 text-slate-400 text-sm">
                            {disc.platform_a && disc.platform_b
                              ? `${PLATFORM_CONFIG[disc.platform_a]?.name || disc.platform_a} → ${PLATFORM_CONFIG[disc.platform_b]?.name || disc.platform_b}`
                              : disc.platform_a || disc.platform_b || '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <SeverityBadge severity={disc.severity} />
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            {disc.detected_at ? new Date(disc.detected_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {!disc.is_resolved && (
                              <button
                                onClick={() => {
                                  setResolvingDisc(disc);
                                  setShowResolveModal(true);
                                }}
                                className="px-3 py-1 bg-green-700 hover:bg-green-600 text-white text-xs rounded-lg transition-all"
                              >
                                Resolve
                              </button>
                            )}
                            {disc.is_resolved && (
                              <span className="text-xs text-green-500 flex items-center justify-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Resolved
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {discrepancies.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      {discSummary.total === 0 ? 'No discrepancies found. Run a sync to check for issues.' : 'No discrepancies match the current filters.'}
                    </div>
                  )}
                </div>
              )}

              {/* Resolve Modal */}
              {showResolveModal && resolvingDisc && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">Resolve Discrepancy</h3>
                    <p className="text-sm text-slate-400 mb-4">
                      {resolvingDisc.description}
                    </p>
                    <textarea
                      placeholder="Resolution notes (optional)"
                      value={resolveNotes}
                      onChange={e => setResolveNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 mb-4 resize-none"
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowResolveModal(false);
                          setResolvingDisc(null);
                          setResolveNotes('');
                        }}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleResolveDiscrepancy}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all"
                      >
                        Mark Resolved
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-white">Settings</h2>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">API Connections</h3>
                <p className="text-slate-400 mb-6">
                  Configure API credentials for each platform using Cloudflare secrets.
                  Run <code className="bg-slate-800 px-2 py-1 rounded">wrangler secret put SECRET_NAME</code> to set each secret.
                </p>

                <div className="space-y-4">
                  {Object.entries(PLATFORM_CONFIG).map(([key, config]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <PlatformIcon platform={key} />
                        <div>
                          <div className="text-white font-medium">{config.name}</div>
                          <div className="text-sm text-slate-500">
                            {getSecretsForPlatform(key).join(', ')}
                          </div>
                        </div>
                      </div>
                      <StatusBadge success={syncStatus[key]?.success} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Integration API</h3>
                <p className="text-slate-400 mb-4">
                  ClarityMate and other tools can query customer data via the integration API.
                  Requires the <code className="bg-slate-800 px-2 py-1 rounded">INTEGRATION_API_KEY</code> secret
                  (passed as <code className="bg-slate-800 px-2 py-1 rounded">X-Integration-Key</code> header).
                </p>
                <div className="space-y-2 text-sm text-slate-400">
                  <p><code className="text-green-400 bg-slate-800 px-2 py-1 rounded">GET /api/integration/customer/:id</code> — by UnifiedBilling ID</p>
                  <p><code className="text-green-400 bg-slate-800 px-2 py-1 rounded">GET /api/integration/customer/at:12345</code> — by Autotask company ID</p>
                </div>
                <div className="mt-4">
                  <code className="block bg-slate-800 p-4 rounded-lg text-green-400 text-sm overflow-x-auto">
                    wrangler secret put INTEGRATION_API_KEY
                  </code>
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Database Setup</h3>
                <p className="text-slate-400 mb-4">
                  Initialize the D1 database using the schema file:
                </p>
                <code className="block bg-slate-800 p-4 rounded-lg text-green-400 text-sm overflow-x-auto">
                  wrangler d1 execute unified-billing-db --file=../D1-SCHEMA.sql
                </code>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Helper function for settings
function getSecretsForPlatform(platform) {
  const secrets = {
    datto_rmm: ['DATTO_RMM_API_KEY', 'DATTO_RMM_API_SECRET'],
    datto_edr: ['DATTO_EDR_API_KEY'],
    datto_saas: ['DATTO_SAAS_API_KEY', 'DATTO_SAAS_API_SECRET'],
    rocketcyber: ['ROCKETCYBER_API_KEY'],
    threatlocker: ['THREATLOCKER_API_KEY'],
    saas_alerts: ['SAAS_ALERTS_API_KEY'],
    pax8: ['PAX8_CLIENT_ID', 'PAX8_CLIENT_SECRET'],
    autotask: ['AUTOTASK_USERNAME', 'AUTOTASK_SECRET', 'AUTOTASK_INTEGRATION_CODE'],
    itglue: ['ITGLUE_API_KEY'],
    darkwebid: ['DARKWEBID_API_KEY'],
    bullphishid: ['BULLPHISHID_API_KEY'],
    inky: ['INKY_API_KEY'],
    cipp: ['CIPP_INSTANCE_URL', 'CIPP_API_KEY'],
  };
  return secrets[platform] || [];
}

// Customer Row Component
function CustomerRow({ customer, onSelect }) {
  const getLicenseCount = (platform) => {
    const license = customer.licenses.find(l => l.platform === platform);
    return license ? license.count : 0;
  };

  const totalLicenses = customer.licenses.reduce((sum, l) => sum + l.count, 0);

  return (
    <tr
      className="hover:bg-slate-800/50 cursor-pointer transition-all"
      onClick={onSelect}
    >
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{customer.displayName || customer.name}</span>
              {customer.discrepanciesHigh > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-red-900/60 text-red-300 font-semibold">
                  <AlertCircle className="w-3 h-3" /> {customer.discrepanciesHigh}
                </span>
              )}
              {customer.discrepanciesHigh === 0 && customer.discrepanciesMedium > 0 && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-yellow-900/60 text-yellow-300 font-semibold">
                  <AlertCircle className="w-3 h-3" /> {customer.discrepanciesMedium}
                </span>
              )}
            </div>
            <div className="text-sm text-slate-500">{customer.platformMappings} platforms mapped</div>
          </div>
        </div>
      </td>
      {Object.keys(PLATFORM_CONFIG).map(platform => (
        <td key={platform} className="px-4 py-4 text-center">
          <span className={getLicenseCount(platform) > 0 ? 'text-white' : 'text-slate-600'}>
            {getLicenseCount(platform) || '-'}
          </span>
        </td>
      ))}
      <td className="px-4 py-4 text-center">
        <span className="text-white font-semibold">{totalLicenses}</span>
      </td>
    </tr>
  );
}

// Customer Details Modal
function CustomerDetailsModal({ customer, onClose }) {
  const [activeTab, setActiveTab] = useState('licenses');
  const [expanded, setExpanded] = useState({});
  const [custDiscs, setCustDiscs] = useState([]);
  const [custDiscsLoading, setCustDiscsLoading] = useState(false);
  const [showCustResolveModal, setShowCustResolveModal] = useState(false);
  const [resolvingCustDisc, setResolvingCustDisc] = useState(null);
  const [custResolveNotes, setCustResolveNotes] = useState('');

  const togglePlatform = (platform) => {
    setExpanded(prev => ({ ...prev, [platform]: !prev[platform] }));
  };

  useEffect(() => {
    if (activeTab === 'discrepancies') {
      setCustDiscsLoading(true);
      api.getDiscrepancies({ customerId: customer.customer.id })
        .then(data => setCustDiscs(data.discrepancies || []))
        .catch(() => {})
        .finally(() => setCustDiscsLoading(false));
    }
  }, [activeTab]);

  async function handleCustResolve() {
    if (!resolvingCustDisc) return;
    try {
      await api.resolveDiscrepancy(resolvingCustDisc.id, 'admin', custResolveNotes);
      setShowCustResolveModal(false);
      setResolvingCustDisc(null);
      setCustResolveNotes('');
      const data = await api.getDiscrepancies({ customerId: customer.customer.id });
      setCustDiscs(data.discrepancies || []);
    } catch (e) {}
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white">
              {customer.customer.displayName || customer.customer.name}
            </h3>
            <p className="text-sm text-slate-400">Customer Details</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-all"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800">
          {['licenses', 'discrepancies'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-3 text-sm font-medium capitalize transition-all border-b-2 ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-slate-500 hover:text-slate-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Licenses by Platform */}
          {activeTab === 'licenses' && (
          <>
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Licenses by Platform</h4>
            <div className="space-y-3">
              {Object.entries(PLATFORM_CONFIG).map(([key, config]) => {
                const platformLicenses = customer.licenses.filter(l => l.platform === key);
                const totalCount = platformLicenses.reduce((sum, l) => sum + l.count, 0);
                const mapping = customer.mappings.find(m => m.platform === key);

                return (
                  <div key={key} className="bg-slate-800 rounded-lg overflow-hidden">
                    <button
                      onClick={() => togglePlatform(key)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/50 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <PlatformIcon platform={key} />
                        <span className="text-white font-medium">{config.name}</span>
                        {mapping && (
                          <span className="text-xs bg-green-900/50 text-green-400 px-2 py-1 rounded">
                            Mapped
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-white font-semibold">{totalCount} {config.licenseType}</span>
                        {expanded[key] ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </button>
                    {expanded[key] && (
                      <div className="px-4 pb-4 border-t border-slate-700">
                        {platformLicenses.length > 0 ? (
                          <div className="mt-3 space-y-2">
                            {platformLicenses.map((license, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-sm"
                              >
                                <span className="text-slate-400">{license.type}</span>
                                <span className="text-white">{license.count}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm mt-3">No licenses for this platform</p>
                        )}
                        {mapping && (
                          <div className="mt-3 pt-3 border-t border-slate-700">
                            <p className="text-xs text-slate-500">
                              Mapped to: {mapping.platformCustomerName} ({mapping.platformCustomerId})
                            </p>
                            {mapping.isAutoMatched && (
                              <p className="text-xs text-slate-500">
                                Auto-matched with {Math.round(mapping.matchConfidence * 100)}% confidence
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          {customer.customer.notes && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">Notes</h4>
              <p className="text-slate-400">{customer.customer.notes}</p>
            </div>
          )}
          </>
          )}

          {/* Discrepancies Tab */}
          {activeTab === 'discrepancies' && (
            <div>
              <h4 className="text-lg font-semibold text-white mb-4">Open Discrepancies</h4>
              {custDiscsLoading ? (
                <div className="flex justify-center py-8"><LoadingSpinner size="lg" /></div>
              ) : custDiscs.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No discrepancies found for this customer.</p>
              ) : (
                <div className="space-y-3">
                  {custDiscs.map(disc => (
                    <div key={disc.id} className={`bg-slate-800 rounded-lg p-4 flex items-start justify-between gap-4 ${disc.is_resolved ? 'opacity-50' : ''}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <SeverityBadge severity={disc.severity} />
                          <span className="text-xs font-mono text-slate-400 bg-slate-700 px-2 py-0.5 rounded">
                            {disc.discrepancy_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-300">{disc.description}</p>
                        {disc.entity_name && (
                          <p className="text-xs text-slate-500 mt-1 font-mono">{disc.entity_name}</p>
                        )}
                        <p className="text-xs text-slate-600 mt-1">
                          {disc.detected_at ? new Date(disc.detected_at).toLocaleString() : ''}
                        </p>
                      </div>
                      {!disc.is_resolved && (
                        <button
                          onClick={() => { setResolvingCustDisc(disc); setShowCustResolveModal(true); }}
                          className="flex-shrink-0 px-3 py-1 bg-green-700 hover:bg-green-600 text-white text-xs rounded-lg transition-all"
                        >
                          Resolve
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Customer Resolve Modal */}
              {showCustResolveModal && resolvingCustDisc && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
                  <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-xl font-bold text-white mb-2">Resolve Discrepancy</h3>
                    <p className="text-sm text-slate-400 mb-4">{resolvingCustDisc.description}</p>
                    <textarea
                      placeholder="Resolution notes (optional)"
                      value={custResolveNotes}
                      onChange={e => setCustResolveNotes(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 mb-4 resize-none"
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => { setShowCustResolveModal(false); setResolvingCustDisc(null); setCustResolveNotes(''); }}
                        className="px-4 py-2 text-slate-400 hover:text-white transition-all"
                      >Cancel</button>
                      <button
                        onClick={handleCustResolve}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all"
                      >Mark Resolved</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Mapping Modal
function MappingModal({ source, customers, onSelect, onClose, onLoadCustomers }) {
  const [search, setSearch] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState(customers);

  useEffect(() => {
    if (customers.length === 0) {
      onLoadCustomers();
    }
  }, []);

  useEffect(() => {
    if (search) {
      const lower = search.toLowerCase();
      setFilteredCustomers(
        customers.filter(c =>
          c.name.toLowerCase().includes(lower) ||
          (c.displayName && c.displayName.toLowerCase().includes(lower))
        )
      );
    } else {
      setFilteredCustomers(customers);
    }
  }, [search, customers]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-800">
          <h3 className="text-xl font-bold text-white">Map Customer</h3>
          <p className="text-sm text-slate-400 mt-1">
            Map "{source.name}" from {PLATFORM_CONFIG[source.platform]?.name}
          </p>
        </div>

        <div className="p-4 border-b border-slate-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search customers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredCustomers.map(customer => (
            <button
              key={customer.id}
              onClick={() => onSelect(customer.id)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-800 transition-all text-left border-b border-slate-800"
            >
              <div>
                <div className="text-white font-medium">{customer.name}</div>
                {customer.displayName && customer.displayName !== customer.name && (
                  <div className="text-sm text-slate-500">{customer.displayName}</div>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-slate-500" />
            </button>
          ))}
          {filteredCustomers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No customers found.
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default UnifiedBilling;
